import React, {
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import Modal from "../../components/UI/Modal";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import Skeleton from "react-loading-skeleton";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import { useDepartment } from "../../contexts/DepartmentMasterContext";
import {
  X,
  Briefcase,
  MoreVertical,
  Building,
  Calendar,
  Users,
  MapPin,
  Award,
  Clock,
  Grid,
  Search,
} from "lucide-react";
import DropdownMenu from "../../components/UI/DropdownMenu";
import ReactPaginate from "react-paginate";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(
  ({
    row,
    openMenu,
    setOpenMenu,
    openModalForEdit,
    handleDeleteDepartmentHOD,
  }) => {
    const buttonRef = useRef(null);

    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() => setOpenMenu(openMenu === row.id ? null : row.id)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>

        {openMenu === row.id && (
          <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
            <button
              onClick={(e) => {
                openModalForEdit(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Edit
            </button>
            <DeleteButton
              style={`text-red-600 hover:text-red-800 px-3 py-2`}
              onDelete={(e) => {
                handleDeleteDepartmentHOD(row.id);
                setOpenMenu(null);
              }}
            />
          </DropdownMenu>
        )}
      </div>
    );
  },
);

const DepartmentHODMasters = React.memo(() => {
  const [departmentHODs, setDepartmentHODs] = useState([]);
  const [hods, setHods] = useState([]);
  const { departments } = useDepartment();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDepartmentHOD, setEditingDepartmentHOD] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    totalPages: 0,
    per_page: 15,
  });
  const token = getToken();
  const [openMenu, setOpenMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isClient, setIsClient] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);

  // Fetch department HODs
  const fetchDepartmentHODs = useCallback(
    async (page = 1) => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `https://hrmspayroll.prepstripe.com/api/department-hods?page=${page}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.data && response.data.data) {
          setDepartmentHODs(response.data.data.data || []);
          setPagination({
            current_page: response.data.data.current_page,
            total: response.data.data.total,
            totalPages: response.data.data.last_page,
            per_page: response.data.data.per_page,
          });
        }
      } catch (error) {
        console.error("Error fetching department HODs:", error);
        setNotification({
          message: "Failed to fetch department HODs",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [token],
  );

  const fetchHods = useCallback(async () => {
    try {
      const response = await axios.get(
        `https://hrmspayroll.prepstripe.com/api/hods`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.data && response.data.success) {
        setHods(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching HODs:", error);
    }
  }, [token]);

  // Load data on component mount
  useEffect(() => {
    fetchDepartmentHODs();
    fetchHods();
  }, [fetchDepartmentHODs, fetchHods]);

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingDepartmentHOD(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((departmentHOD) => {
    setEditingDepartmentHOD(departmentHOD);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingDepartmentHOD(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (departmentHOD) => {
      setLoading(true);
      try {
        // const token = localStorage.getItem('token');
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        if (editingDepartmentHOD) {
          // console.log("department HOD payload", departmentHOD);

          // Convert FormData to object
          // console.log(departmentHOD instanceof FormData)
          const departmentHODData =
            departmentHOD instanceof FormData
              ? convertFormDataToObject(departmentHOD)
              : departmentHOD;

          // console.log("Payload being sent:", { ...editingDepartmentHOD, ...departmentHODData });

          await axios.put(
            `https://hrmspayroll.prepstripe.com/api/department-hods/${editingDepartmentHOD.id}`,
            { ...editingDepartmentHOD, ...departmentHODData },
            { headers },
          );
          setMessage("Department HOD updated successfully.");
          setNotification({
            message: "Department HOD updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create department HOD", departmentHOD)
          await axios.post(
            "https://hrmspayroll.prepstripe.com/api/department-hods",
            departmentHOD,
            { headers },
          );
          setMessage("Department HOD added successfully.");
          setNotification({
            message: "Department HOD added successfully.",
            type: "success",
          });
        }
        closeModal();
        fetchDepartmentHODs(pagination.current_page); // Refresh the list
      } catch (err) {
        if (err.response?.data?.errors) {
          setFormErrors(err.response.data.errors);
        } else if (err.response?.data?.message) {
          // Handle specific error messages like "Selected user is not assigned the HOD role."
          setNotification({
            message: err.response.data.message,
            type: "error",
          });
        } else {
          setNotification({
            message: `Unexpected error during submission: ${err.message}`,
            type: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [
      editingDepartmentHOD,
      closeModal,
      fetchDepartmentHODs,
      pagination.current_page,
    ],
  );

  // Memoize department HODs for the table
  const departmentHODForTable = useMemo(
    () =>
      (Array.isArray(departmentHODs) ? departmentHODs : []).map(
        (departmentHOD) => ({
          ...departmentHOD,
          department_name:
            departmentHOD?.department?.name || "Unknown Department",
          hod_name: departmentHOD?.hod?.name || "Unknown HOD",
          hod_email: departmentHOD?.hod?.email || "No Email",
        }),
      ),
    [departmentHODs],
  );

  const filteredDepartmentHODs = useMemo(() => {
    return departmentHODForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return (
        item.hod_name.toLowerCase().includes(searchTerm) ||
        item.hod_email.toLowerCase().includes(searchTerm) ||
        item.department_name.toLowerCase().includes(searchTerm)
      );
    });
  }, [departmentHODForTable, searchQuery]);

  const handleDeleteDepartmentHOD = async (id) => {
    try {
      //   const token = localStorage.getItem('token');
      await axios.delete(
        `https://hrmspayroll.prepstripe.com/api/department-hods/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setNotification({
        message: "Department HOD deleted successfully",
        type: "success",
      });
      fetchDepartmentHODs(pagination.current_page); // Refresh the list
    } catch (error) {
      setNotification({
        message: "Failed to delete department HOD",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        label: "Department",
        field: "department_name",
        render: (row) => (
          <span
            className="block max-w-[200px] truncate"
            title={row.department_name}
          >
            {row.department_name}
          </span>
        ),
      },
      {
        label: "HOD Name",
        field: "hod_name",
        render: (row) => (
          <span className="block max-w-[200px] truncate" title={row.hod_name}>
            {row.hod_name}
          </span>
        ),
      },
      {
        label: "HOD Email",
        field: "hod_email",
        render: (row) => (
          <span className="block max-w-[200px] truncate" title={row.hod_email}>
            {row.hod_email}
          </span>
        ),
      },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteDepartmentHOD={handleDeleteDepartmentHOD}
          />
        ),
      },
    ],
    [openMenu, openModalForEdit, handleDeleteDepartmentHOD],
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchDepartmentHODs(newPage);
    }
  };

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      {/* notification compo */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">
          Department HOD Masters
        </h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by HOD name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Button */}
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
        >
          Add Department HOD
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Skeleton Loader */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      ) : (
        <div>
          <ReusableTable
            data={filteredDepartmentHODs}
            loading={isLoading}
            columns={columns}
            filterable={false}
            excludeFilterColumns={["actions"]}
          />

          {/* Pagination */}
          {/* {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center mt-4">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className={`px-4 py-2 rounded-md mx-1 ${
                  pagination.current_page === 1 ? "bg-gray-300" : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Previous
              </button>
              
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 rounded-md mx-1 ${
                    pagination.current_page === page ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.totalPages}
                className={`px-4 py-2 rounded-md mx-1 ${
                  pagination.current_page === pagination.totalPages ? "bg-gray-300" : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Next
              </button>
            </div>
          )} */}

          {!isFiltering && departmentHODs?.length > 0 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={(event) => handlePageChange(event.selected + 1)}
              forcePage={currentPage - 1}
              pageRangeDisplayed={5}
              disabledClassName="opacity-50 cursor-not-allowed"
              disabledLinkClassName="pointer-events-none"
              pageCount={pagination.totalPages}
              previousLabel="< Prev"
              previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
              previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
              nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center justify-center mt-4 font-poppins"
              pageClassName="mx-1 border rounded dark:text-white font-poppins"
              pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
              activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
              activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
            />
          )}
        </div>
      )}

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={
          editingDepartmentHOD ? "Edit Department HOD" : "Add Department HOD"
        }
      >
        <div className="">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleFormSubmit(formData);
            }}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Department <span className="text-red-500">*</span>
                </label>
                <select
                  name="department_id"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  defaultValue={editingDepartmentHOD?.department_id || ""}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {formErrors.department_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.department_id}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  HOD <span className="text-red-500">*</span>
                </label>
                <select
                  name="hod_id"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  defaultValue={editingDepartmentHOD?.hod_id || ""}
                >
                  <option value="">Select HOD</option>
                  {hods.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
                {formErrors.hod_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.hod_id}
                  </p>
                )}
              </div>

              {/* Display current mapping info if editing */}
              {editingDepartmentHOD && (
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Current Mapping:
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Department:</span>{" "}
                      {editingDepartmentHOD.department_name || "Unknown"}
                    </div>
                    <div>
                      <span className="font-medium">HOD:</span>{" "}
                      {editingDepartmentHOD.hod_name || "Unknown"} (
                      {editingDepartmentHOD.hod_email || "No email"})
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end items-center gap-2 mt-6">
              <button
                type="button"
                onClick={closeModal}
                className="px-6 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading
                  ? "Saving..."
                  : editingDepartmentHOD
                    ? "Update"
                    : "Save"}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
});

export default DepartmentHODMasters;
